import { CompleteOperationType } from 'model/alerts.model';
import { useCallback, useState } from 'react';
import { TaskService } from 'service/task.service';

interface UseTaskFieldUpdatesProps {
  selectedData: any;
  completeOperation: CompleteOperationType;
  onUpdate?: (updatedTaskData?: any) => void;
}

export const useTaskFieldUpdates = ({
  selectedData,
  completeOperation,
  onUpdate,
}: UseTaskFieldUpdatesProps) => {
  const [updating, setUpdating] = useState(false);

  const updateTaskField = useCallback(
    async (field: 'assignee' | 'status' | 'priority', value: any) => {
      if (!selectedData?.id) {
        completeOperation('update', 'Task ID is missing', true);
        return;
      }

      setUpdating(true);
      try {
        const updateData: any = {
          subject: selectedData?.subject,
        };

        if (field === 'assignee') {
          if (Array.isArray(value) && value.length > 0) {
            updateData.assignedTo = value.map((assignee: any) => ({
              Name: assignee?.Name || '',
              Id: assignee?.Id || '',
              Email: assignee?.Email || '',
            }));
          } else if (value && value.Id) {
            updateData.assignedTo = [
              {
                Name: value?.Name || '',
                Id: value?.Id || '',
                Email: value?.Email || '',
              },
            ];
          } else {
            updateData.assignedTo = null;
          }
        } else {
          if (Array.isArray(selectedData?.assignedTo) && selectedData.assignedTo.length > 0) {
            updateData.assignedTo = selectedData.assignedTo.map((assignee: any) => ({
              Name: assignee?.Name || assignee?.name || '',
              Id: assignee?.Id || assignee?.id || '',
              Email: assignee?.Email || assignee?.email || '',
            }));
          } else if (selectedData?.assignedTo?.Id || selectedData?.assignTo?.Id) {
            const assignee = selectedData?.assignedTo || selectedData?.assignTo;
            updateData.assignedTo = [
              {
                Name: assignee?.Name || assignee?.name || '',
                Id: assignee?.Id || assignee?.id || '',
                Email: assignee?.Email || assignee?.email || '',
              },
            ];
          }
        }

        if (field === 'status') {
          updateData.status = value;
        } else {
          updateData.status = selectedData?.status;
        }

        if (field === 'priority') {
          updateData.priority = value;
        } else {
          const currentPriority = Array.isArray(selectedData?.priority)
            ? selectedData.priority[0]
            : selectedData?.priority || 'Normal';
          updateData.priority = currentPriority;
        }

        if (selectedData?.dueDate || selectedData?.date) {
          updateData.dueDate = selectedData.dueDate || selectedData.date;
        }
        if (selectedData?.description) {
          updateData.description = selectedData.description;
        }
        if (selectedData?.recordTypeName) {
          updateData.recordTypeName = selectedData.recordTypeName;
        }

        const res = await TaskService.updateTask(selectedData.id, updateData);

        const updatedTaskData = {
          ...selectedData,
          ...(field === 'status' && { status: value }),
          ...(field === 'priority' && { priority: value }),
          ...(field === 'assignee' && {
            assignedTo: updateData.assignedTo,
          }),
        };

        completeOperation('update', `${field} updated successfully.`);

        onUpdate?.(updatedTaskData);
        return res;
      } catch (err: any) {
        completeOperation(
          'update',
          err?.response?.data?.error || err?.message || `Failed to update ${field}`,
          true,
        );

        throw err;
      } finally {
        setUpdating(false);
      }
    },
    [selectedData, onUpdate],
  );

  return {
    updating,
    updateTaskField,
  };
};
